import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

/**
 * Send the user's own words on an outreach they're a party to — DIRECTLY, from the conversation viewer.
 *
 * No chat, no agent relay: this POSTs to `/api/outreach-send`, a thin forwarder to the native-data
 * bridge's `send-outreach-message` action (orion-web's own service identity cannot call OutreachService,
 * so the write goes through the app-identity bridge, exactly as the outreach READ does). The server
 * resolves the caller's side (source pushes, target replies) and there is no approval gate — the words
 * are the user's own.
 *
 * Returns `'sent'` on success (or a server-side dedupe of an identical last message) and `'failed'` on
 * any refusal or transport error, which the viewer surfaces as an inline "couldn't send" without ever
 * falling back to the chat.
 */
export type OutreachSendResult = 'sent' | 'failed';

export async function sendOutreachMessage(outreachId: string, text: string): Promise<OutreachSendResult> {
  const trimmed = text.trim();
  if (!outreachId || !trimmed) return 'failed';
  try {
    const res = await apiFetch(`${API_BASE}/api/outreach-send`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outreachId, messageText: trimmed }),
    });
    if (!res.ok) return 'failed';
    const data = (await res.json()) as { sent?: boolean };
    return data.sent ? 'sent' : 'failed';
  } catch {
    return 'failed';
  }
}
